<template>
    <v-row>
        <v-col cols="12" md="8" class="d-md-flex justify-center justify-md-space-between">
            <city-airport-autocomplete
                ref="firstLocation"
                v-model="locations.from"
                single-line
                filled
                label="departure_location"
                :rules="[v => !!v || `${$t('departure_location')} ${$t('validation.required')}`]"
                :default-cities="[]"
                class="flex-grow-1 location-field"
            />
            <div class="text-center swap-btn">
                <v-btn
                    :icon="isMobile ? 'mdi-swap-vertical-bold' : 'mdi-swap-horizontal-bold'"
                    variant="text"
                    @click="swapLocations"
                />
            </div>
            <city-airport-autocomplete
                ref="secondLocation"
                v-model="locations.to"
                single-line
                filled
                label="arrival_location"
                :rules="[v => !!v || `${$t('arrival_location')} ${$t('validation.required')}`]"
                :default-cities="[]"
                class="flex-grow-1 location-field"
            />
        </v-col>
        <v-col cols="12" md="4">
            <!-- eslint-disable vue/no-custom-modifiers-on-v-model -->
            <vc-date-picker
                v-model.string="dateTime"
                :masks="{modelValue: 'YYYY-MM-DD'}"
                :locale="$i18n.locale"
                :min-date="minDateTime"
                :first-day-of-week="$conf.public.firstDayOfWeek || undefined"
                is-required
            >
                <template v-slot="{inputEvents}">
                    <div v-on="inputEvents">
                        <v-text-field
                            :label="$t('transfers.departure_date')"
                            readonly
                            single-line
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            bg-color="white"
                            prepend-inner-icon="mdi-calendar"
                            :model-value="formattedDate"
                        />
                    </div>
                </template>
            </vc-date-picker>
        </v-col>
    </v-row>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import CityAirportAutocomplete from '@/components/flights/search/forms/CityAirportAutocomplete.vue'
    import {FlightSearchFormMixin} from '@/mixins/Flights/FlightSearchFormMixin.vue'

    @Component({
        components: {CityAirportAutocomplete},
    })
    export class OneWay extends mixins(FlightSearchFormMixin) {
        dateTime = null

        @Watch('locations.to')
        onChangeTo(to) {
            if (to) {
                this.setDeparture(this.formIndex + 1, to)
            }
        }

        mounted() {
            this.newRoute(this.formIndex)
        }

        setDeparture(formIndex, departure) {
            if (this.formIndex === formIndex) {
                this.locations.from = departure
            }
        }

        newRoute(formIndex) {
            if (this.formIndex === formIndex - 1 && this.locations.to) {
                this.setDeparture(this.formIndex + 1, this.locations.to)
            }
        }

        initDates() {
            if (
                !this.isEmptyRoute &&
                this.$dateFns.isAfter(
                    this.$dateFns.parseISO(flightsRuntimeStore.routes[this.formIndex].date),
                    new Date()
                )
            ) {
                this.dateTime = flightsRuntimeStore.routes[this.formIndex].date
            } else {
                this.dateTime = this.$dateFns.format(this.$dateFns.addDays(new Date(), 1))
            }
        }

        getRoutes() {
            return [
                {
                    departure: this.locations.from,
                    arrival: this.locations.to,
                    date: this.dateTime,
                },
            ]
        }

        initDestinations() {
            if (flightsRuntimeStore.routes.length >= this.formIndex + 1) {
                this.locations.from = flightsRuntimeStore.routes[this.formIndex].departure
                this.$refs.firstLocation.selectCity(this.locations.from)
                this.locations.to = flightsRuntimeStore.routes[this.formIndex].arrival
                this.$refs.secondLocation.selectCity(this.locations.to)
            }
        }

        get firstDayOfWeekByLocale() {
            return this.$dateFns.localeOptions[this.locale].weekStartsOn
        }

        get isEmptyRoute() {
            return flightsRuntimeStore.routes.length < this.formIndex + 1
        }

        get locale() {
            return this.$i18n.locale
        }

        get minDateTime() {
            return this.$dateFns.format(new Date())
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get formattedDate() {
            return this.dateTime !== null ? this.$filters.dateFormat(this.dateTime) : ''
        }
    }

    export default OneWay
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
        .location-field {
            flex-basis: 100%;
        }
    }

    :deep(.date-range-input) {
        @media (min-width: 1636px) and (max-width: 1840px) {
            input {
                font-size: 14px;
            }
        }
        @media (min-width: 1400px) and (max-width: 1635px) {
            input {
                font-size: 11px;
            }
        }
        @media (min-width: 1325px) and (max-width: 1399px) {
            input {
                font-size: 10px;
            }
        }
        @media (min-width: 1245px) and (max-width: 1324px) {
            input {
                font-size: 9px;
            }
        }
        @media (min-width: 1165px) and (max-width: 1244px) {
            input {
                font-size: 9px;
            }
        }
        @media (min-width: 1090px) and (max-width: 1164px) {
            input {
                font-size: 8px;
            }
        }
        @media (min-width: 960px) and (max-width: 1089px) {
            input {
                font-size: 7px;
            }
        }
    }
</style>
